import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';
import {useHistory} from 'react-router-dom';

import {CenteredSpinner} from 'app/components/CenteredSpinner';
import {api} from 'app/modules/Remote';
import {ApiFeature} from 'app/modules/Remote/Feature';
import colors from 'app/utils/colorUtils';
import {useUserInfo} from 'app/utils/featureFlags';
import {formatDate} from 'app/utils/imageryUtils';

import {ApiAgentRun, ApiAgentWorkflow, useAgentRunQuery} from './hooks';
import NotesPreview from './NotePreview';
import {StepDialog} from './StepDialog';
import * as cs from './styles.styl';
import {parseAgentResponse} from './utils';

type Props = {
  workflow: ApiAgentWorkflow;
  runId: string;
  features: ApiFeature[];
};

const FEATURE_COLLECTION_ID = 4243;

export const RunView: React.FC<Props> = ({workflow, runId, features}) => {
  const [selectedFeature, _setSelectedFeature] = React.useState<ApiFeature>(features[0]);
  const [agentRun, setAgentRun] = React.useState<ApiAgentRun | null>(null);
  const {data: existingAgentRun, isFetching: isFetchingExistingAgentRun} = useAgentRunQuery(
    workflow.id,
    runId
  );

  React.useEffect(() => {
    if (existingAgentRun) {
      setAgentRun(existingAgentRun);
    }
  }, [existingAgentRun]);

  const searchParams = new URLSearchParams(window.location.search);
  const isNewRun = searchParams.get('new');

  const streamComplete = agentRun?.messages.some((m) => m.type === 'note');

  React.useEffect(() => {
    async function kickoffRun() {
      if (isNewRun) {
        const chatStream = api.agent.sendMessage(features[0].id, workflow.objectives, runId);

        const defaultAgentRun: ApiAgentRun = {
          status: 'in_progress',
          startedAt: new Date().toISOString(),
          endedAt: null,
          userId: '',
          runId,
          workflowId: workflow.id,
          featureIds: features.map((f) => f.id),
          messages: [],
        };
        setAgentRun(defaultAgentRun);

        for await (const chunk of chatStream) {
          const messages = parseAgentResponse(chunk);
          setAgentRun((prevRun) => {
            if (!prevRun) return defaultAgentRun;
            return {
              ...prevRun,
              messages: [...prevRun.messages, ...messages],
            };
          });
        }
      }
    }
    kickoffRun(); // Kick off the run if not already running
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [organization] = useUserInfo();
  const history = useHistory();

  const agentMessages = agentRun?.messages;

  if (isFetchingExistingAgentRun && !isNewRun) {
    return (
      <div className={cs.page}>
        <CenteredSpinner />
      </div>
    );
  }
  if (!agentRun && !isNewRun) {
    return <div>No agent run found.</div>;
  }
  return (
    <div className={cs.page}>
      {agentMessages && features && (
        <>
          <B.Breadcrumbs
            items={[
              {
                text: 'Workflows',
                onClick: () => {
                  history.push(`/${organization.get('id')}/agent/workflows`);
                },
              },
              {
                text: workflow.name,
                onClick: () => {
                  history.push(`/${organization.get('id')}/agent/workflows/${workflow.id}`);
                },
              },
              {
                text: 'Run ' + formatDate(agentRun.startedAt),
                onClick: () => {
                  history.push(
                    `/${organization.get('id')}/agent/workflows/${workflow.id}/runs/${runId}`
                  );
                },
              },
            ]}
          />
          <div style={{whiteSpace: 'pre-line', marginBottom: '2rem'}}>
            <React.Fragment>
              <StepDialog
                defaultOpen={!!isNewRun}
                collapsible={true}
                stepTitle={'Run Agent'}
                className={cs.stepDialogAutoScroll}
                agentLines={agentMessages.filter((line) => line.type === 'text')}
                isStreaming={!streamComplete}
                autoScroll={true}
              />
            </React.Fragment>
          </div>

          <NotesPreview
            feature={I.fromJS(selectedFeature)}
            featureCollectionId={FEATURE_COLLECTION_ID}
            agentResponse={agentMessages}
            title={
              <span className={cs.sectionHeader}>
                {
                  <B.Icon
                    icon={streamComplete ? 'tick' : 'time'}
                    intent={streamComplete ? 'success' : 'none'}
                    color={colors.purple}
                  />
                }
                <B.H6 className={cs.sectionTitle}>Review Notes</B.H6>
              </span>
            }
          />
        </>
      )}
    </div>
  );
};
